import { isNil } from 'lodash-es'
import { Camera } from '@/types/camera'
import {
  Device,
  EdgeCapabilities,
  EdgeClientLastActionError,
  Gpu,
  TowerStatus,
  TrackingStatus,
  VlmStatus
} from '@/types/device'

// Helper to extract typed values from Thingsboard device attributes
// Example: getDeviceAttr<string>(device, 'software_version') // returns "1.5.1"
//
// Device attribute structure:
// {
//   lastUpdateTs: 1749209385209,
//   key: "software_version",
//   value: "1.5.1"
// }
export function getDeviceAttr<T>(
  device: Device,
  attrKey: string
): T | undefined {
  const attribute = device.attributes?.find((attr) => attr.key === attrKey)
  return attribute?.value as T | undefined
}

export function getDeviceAttrLastUpdated(device: Device, attrKey: string) {
  return device.attributes?.find((attr) => attr.key === attrKey)?.lastUpdateTs
}

export const getEdgeClientVersion = (device: Device) => {
  return getDeviceAttr<string>(device, 'software_version')
}

// An older edge never overwrites edge_capabilities, so a stale one names another software_version.
export const getEdgeCapabilities = (device: Device) => {
  const capabilities = getDeviceAttr<EdgeCapabilities>(
    device,
    'edge_capabilities'
  )

  return capabilities?.software_version === getEdgeClientVersion(device)
    ? capabilities
    : undefined
}

export const getDeepStreamVersion = (device: Device) => {
  return getDeviceAttr<string>(device, 'deepstream_version')
}

export const isJetsonDevice = (device: Device) => {
  return getDeviceAttr<boolean>(device, 'is_jetson')
}

export const getDevicesCameras = (device: Device) => {
  return getDeviceAttr<Camera[]>(device, 'cameras') || []
}

export const getVlmStatus = (device: Device) => {
  return getDeviceAttr<VlmStatus>(device, 'vlm_attributes')
}

export const getTrackingStatus = (device: Device) => {
  return getDeviceAttr<TrackingStatus>(device, 'tracking_attributes')
}

export const getTowerStatus = (device: Device) => {
  return getDeviceAttr<TowerStatus>(device, 'tower_attributes')
}

export const getDeviceGpus = (device: Device) => {
  return getDeviceAttr<Gpu[]>(device, 'gpus') || []
}

// A GPU whose NVML handle cannot be opened keeps its index with every other field null.
export const isGpuUnreadable = (gpu: Gpu) => {
  return [gpu.name, gpu.uuid, gpu.memory_total_mb].every(isNil)
}

export const getVlmGpuIndexes = (device: Device) => {
  return getVlmStatus(device)?.gpu_index ?? []
}

// Edges before gpus v2 send no reserved_by; the backend then reserves the saved
// AI Agent settings first and falls back to vlm_attributes.gpu_index.
export const isGpuReservedForVlm = (
  device: Device,
  gpu: Gpu,
  savedVlmGpus?: number[]
) => {
  return gpu.reserved_by
    ? gpu.reserved_by.includes('vlm')
    : (savedVlmGpus ?? getVlmGpuIndexes(device)).includes(gpu.index)
}

const toActionError = (
  lastAction: Device['last_client_actions']['update']
): EdgeClientLastActionError | null => {
  if (!lastAction || lastAction.status !== 'failed') {
    return null
  }

  return {
    action: lastAction.action,
    category: lastAction.error_category,
    detail: lastAction.error_detail
  }
}

export const getLastEdgeClientActionErrors = (
  device: Device
): {
  update: EdgeClientLastActionError | null
  restart: EdgeClientLastActionError | null
} => {
  return {
    update: toActionError(device.last_client_actions.update),
    restart: toActionError(device.last_client_actions.restart)
  }
}
